import type { ReactNode } from 'react';

export interface StatTileProps {
  label: string;
  /** Pre-formatted value, or null when there is no data to show. */
  value: string | null;
  hint?: string;
  /** Draws attention without relying on colour alone — see SPEC §5.1.2 UI note. */
  emphasis?: 'normal' | 'warn' | 'good';
  icon?: ReactNode;
}

/** Shared by both brass strips — only the edge they pin to differs. */
const BRASS_STRIP = 'absolute inset-x-3 h-0.5 bg-gradient-to-r from-transparent via-brookfields-gold to-transparent';

const EMPHASIS: Record<NonNullable<StatTileProps['emphasis']>, string> = {
  // A real elevation step, not slate-900 reused — a tile looks lifted off the
  // page rather than merely outlined on top of it.
  normal: 'border-slate-800 bg-surface-2',
  // warn/good's full-perimeter ring IS the alert (SPEC §5.1.2 — never colour
  // alone), so it stays exactly as strong here as everywhere else it appears.
  // The brass strip below sits ON TOP of it rather than replacing any side of
  // it, which is what first shipping the strip only on 'normal' got backwards:
  // the property's own identity should not be the one thing missing from the
  // tiles people look at because something is wrong.
  warn: 'border-2 border-red-500/70 bg-red-950/40',
  good: 'border-2 border-emerald-500/60 bg-emerald-950/30',
};

/**
 * A single dashboard figure. Renders an explicit dash for missing data rather than
 * a zero — "no tickets were acknowledged" and "acknowledged instantly" are very
 * different things, and a zero would quietly claim the second.
 */
export function StatTile({ label, value, hint, emphasis = 'normal', icon }: StatTileProps) {
  return (
    <div className={`relative rounded-xl border p-4 ${EMPHASIS[emphasis]}`}>
      {/* The brass motif — the header rule, the login hero, StatTile's own
          previous top edge — on every tile, not only the ones with nothing to
          report, and on BOTH edges: a strip on the top alone read as a stray
          highlight rather than a deliberate frame once tiles ran wide enough
          to show top and bottom as visually separate lines. An absolutely
          positioned child paints above its parent's own border in normal
          stacking order, so both sit in front of whichever colour is
          signalling the tile's state without touching it: the alert ring
          underneath is one continuous red or emerald line, full strength,
          exactly as SPEC §5.1.2 requires. */}
      <div aria-hidden="true" className={`${BRASS_STRIP} top-0`} />
      <div aria-hidden="true" className={`${BRASS_STRIP} bottom-0`} />
      <div className="flex items-center gap-2 text-sm text-slate-400">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-1 font-brand text-3xl font-semibold tabular-nums text-slate-50">
        {value ?? <span className="text-slate-500">—</span>}
      </p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
